import React from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, Eye, Users, BarChart3, Shield,
  BrainCircuit, ClipboardList, Mic, CalendarCheck,
  ArrowRight, Target, Sparkles, UserCheck, Bell,
  Video, Swords, Layout
} from 'lucide-react';

const personas = [
  {
    id: 'cro',
    label: "CRO / VP Sales",
    icon: TrendingUp,
    headline: "A forecast you can defend. A pipeline you can trust.",
    benefits: [
      { icon: Layout, title: "Command Center", desc: "Executive dashboard with pipeline health scoring, WOW insights, and deal deep-dive — all in one view." },
      { icon: BarChart3, title: "Evidence-Backed Forecasts", desc: "Commit, best case, and pipeline numbers scored by AI from real buyer signals — not rep opinions." },
      { icon: Eye, title: "No More Fake Deals", desc: "AI surfaces single-threaded, stalled, and 'happy ears' deals. MEDDIC scoring auto-populated from signals." },
      { icon: Shield, title: "Business Continuity", desc: "When a rep leaves, their deal context, meeting history, and relationships stay. No pipeline goes dark." },
      { icon: Target, title: "Forecast Advisor", desc: "Ask questions about your pipeline in natural language. Get answers backed by real data and evidence." },
      { icon: Bell, title: "Weekly CRO Digest", desc: "Pipeline health summary, top risk deals, and recommended actions delivered to Slack every week." },
    ],
  },
  {
    id: 'manager',
    label: "Sales Manager",
    icon: ClipboardList,
    headline: "Coach with evidence. Track progression. Build a team that compounds.",
    benefits: [
      { icon: BrainCircuit, title: "AI Coaching from Calls", desc: "Coaching insights generated from actual call recordings and meeting transcripts — per rep, backed by data." },
      { icon: UserCheck, title: "Rep Benchmarking", desc: "Compare rep performance with evidence-based metrics. Track improvement over time, not subjective reviews." },
      { icon: Target, title: "Deal Inspection", desc: "Full activity timeline, buyer engagement score, and every signal per deal. No chasing reps for updates." },
      { icon: ClipboardList, title: "Action Item Tracking", desc: "Assign follow-ups from meetings. Track to resolution. Nothing falls through the cracks." },
      { icon: Swords, title: "Competitive Battlecards", desc: "AI-generated battlecards based on win/loss data. Know how to position against each competitor." },
      { icon: Bell, title: "Risk Alerts in Slack", desc: "Get notified when deals stall, engagement drops, or buyer response time spikes. Act before it's too late." },
    ],
  },
  {
    id: 'rep',
    label: "Sales Rep",
    icon: Mic,
    headline: "Your CRM updates itself. You just sell.",
    benefits: [
      { icon: Video, title: "Meeting Auto-Capture", desc: "Every meeting auto-ingested, transcribed, and summarised. CRM updates written without lifting a finger." },
      { icon: CalendarCheck, title: "Meeting Prep in Seconds", desc: "AI briefs with deal context, stakeholder map, competitor intel, and suggested talking points." },
      { icon: Mic, title: "Voice & Chat CRM Updates", desc: "Drop a voice note or type naturally. NoQ5 maps it to the right CRM fields and objects automatically." },
      { icon: Sparkles, title: "Auto Follow-Ups", desc: "Post-meeting summaries and draft follow-up emails generated automatically. One click to send." },
      { icon: BrainCircuit, title: "Real-Time Deal Coaching", desc: "Objection handling suggestions, competitive intel, and next-step recommendations during meetings." },
      { icon: Users, title: "Smart Contact Creation", desc: "Drop a screenshot or business card — NoQ5 extracts contacts, creates records, and associates to companies." },
    ],
  },
];

interface PersonaTabsProps {
  onScrollToDemo: () => void;
}

export const PersonaTabs = ({ onScrollToDemo }: PersonaTabsProps) => {
  return (
    <section className="w-full py-20 md:py-32 bg-muted/30">
      <div className="container px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Built for <span className="text-gradient-teal">your role</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Whether you're defending the number to the board, coaching a team, or closing deals — NoQ5 works for you.
          </p>
        </motion.div>

        <Tabs defaultValue="cro" className="max-w-5xl mx-auto min-w-0">
          <TabsList className="w-full grid grid-cols-3 bg-card border border-border rounded-sm p-1 h-auto gap-1 mb-10">
            {personas.map((p) => (
              <TabsTrigger
                key={p.id}
                value={p.id}
                className="min-w-0 flex items-center justify-center gap-1.5 px-1 sm:px-6 py-3 rounded-sm text-[11px] sm:text-sm font-semibold whitespace-normal leading-tight data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-colors"
              >
                <p.icon className="w-4 h-4" />
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {personas.map((p) => (
            <TabsContent key={p.id} value={p.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-10">
                  {p.headline}
                </h3>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {p.benefits.map((b, i) => (
                    <div
                      key={i}
                      className="bg-card rounded-2xl p-5 border border-border hover:border-primary/30 hover:shadow-card transition-all duration-300"
                    >
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                        <b.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h4 className="font-semibold text-foreground mb-2">{b.title}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="flex justify-center mt-10">
                  <Button
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl shadow-button hover:shadow-glow transition-all duration-300 group"
                    onClick={onScrollToDemo}
                  >
                    Book a Demo
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};
